import { useState } from "react";
import { Loader2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { useAuthedHttp, errMsg, img } from "@/lib/api";

export function MediaInput({ value, onChange, accept = "image/*", testid }) {
  const call = useAuthedHttp();
  const [busy, setBusy] = useState(0);
  const up = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    setBusy(1);
    try {
      const r = await call("post", "/admin/upload", fd, { onUploadProgress: (e) => setBusy(Math.max(1, Math.round((e.loaded / (e.total || 1)) * 100))) });
      onChange(r.url);
      toast.success("Uploaded");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(0); }
  };
  const isVideo = /\.mp4($|\?)/i.test(value || "") || accept.includes("video");
  return (
    <div className="flex gap-3 items-start" data-testid={testid}>
      {value && (isVideo ? <video src={img(value)} className="w-16 h-24 object-cover bg-black" muted /> : <img src={img(value)} alt="" className="w-16 h-16 object-cover bg-black" />)}
      <div className="flex-1 space-y-2">
        <input className="field text-xs" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Paste URL or upload" data-testid={`${testid}-url`} />
        <label className="chip inline-flex items-center gap-2 cursor-pointer">
          {busy ? <><Loader2 className="w-3 h-3 animate-spin" />{busy}%</> : <><Upload className="w-3 h-3" />Upload</>}
          <input type="file" accept={accept} className="hidden" data-testid={`${testid}-file`} onChange={(e) => e.target.files[0] && up(e.target.files[0])} />
        </label>
      </div>
    </div>
  );
}

export function ImagesInput({ value = [], onChange, testid }) {
  const [adding, setAdding] = useState("");
  return (
    <div data-testid={testid}>
      <div className="flex flex-wrap gap-2 mb-3">
        {value.map((u, i) => (
          <div key={i} className="relative w-20 h-24 bg-black">
            <img src={img(u)} alt="" className="w-full h-full object-cover" />
            <button type="button" onClick={() => onChange(value.filter((_, k) => k !== i))} className="absolute top-1 right-1 w-5 h-5 bg-black/80 grid place-items-center" data-testid={`${testid}-remove-${i}`}><X className="w-3 h-3" /></button>
            <span className="absolute bottom-0 left-0 bg-black/70 text-[9px] px-1 font-mono">{i === 0 ? "MAIN" : i}</span>
          </div>
        ))}
      </div>
      <MediaInput value={adding} onChange={(u) => { if (u.startsWith("/api") || u.startsWith("http")) { onChange([...value, u]); setAdding(""); } else setAdding(u); }} testid={`${testid}-add`} />
    </div>
  );
}

export function Field({ f, value, onChange }) {
  const tid = `field-${f.name}`;
  const common = { className: "field", value: value ?? "", onChange: (e) => onChange(e.target.value), "data-testid": tid, required: f.required };
  switch (f.type) {
    case "textarea": return <textarea rows={3} {...common} />;
    case "number": return <input type="number" step="any" {...common} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} />;
    case "bool": return <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} data-testid={tid} className="accent-[#C6FF00] w-4 h-4" />{f.hint || "Enabled"}</label>;
    case "select": return <select {...common}>{(f.options || []).map((o) => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}</select>;
    case "list": return <input {...common} value={(value || []).join(", ")} onChange={(e) => onChange(e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} placeholder="comma, separated" />;
    case "multi": return <div className="flex flex-wrap gap-2" data-testid={tid}>{f.options.map((o) => <button type="button" key={o} onClick={() => onChange((value || []).includes(o) ? value.filter((x) => x !== o) : [...(value || []), o])} className={`chip ${(value || []).includes(o) ? "on" : ""}`}>{o}</button>)}</div>;
    case "image": return <MediaInput value={value} onChange={onChange} testid={tid} />;
    case "video": return <MediaInput value={value} onChange={onChange} accept="video/mp4" testid={tid} />;
    case "images": return <ImagesInput value={value} onChange={onChange} testid={tid} />;
    default: return <input {...common} />;
  }
}
